import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { LoginPage } from '@/pages/auth/LoginPage'
import { SignupPage } from '@/pages/auth/SignupPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage'
import { ResetPasswordInvalidPage } from '@/pages/auth/ResetPasswordInvalidPage'
import { ResetPasswordSentPage } from '@/pages/auth/ResetPasswordSentPage'
import { InviteAlreadyUsedPage } from '@/pages/auth/InviteAlreadyUsedPage'
import { InviteInvalidPage } from '@/pages/auth/InviteInvalidPage'
import { DashboardPage } from '@/pages/dashboard/DashboardPage'
import { MetricsDashboardPage } from '@/pages/dashboard/MetricsDashboardPage'
import { DeviceListPage } from '@/pages/devices/DeviceListPage'
import { DeviceDetailPage } from '@/pages/devices/DeviceDetailPage'
import { DeviceEditPage } from '@/pages/devices/DeviceEditPage'
import { DeviceNewPage } from '@/pages/devices/DeviceNewPage'
import { DeviceHistoryPage } from '@/pages/devices/DeviceHistoryPage'
import { DeviceClaimCodesPage } from '@/pages/devices/DeviceClaimCodesPage'
import { AlertListPage } from '@/pages/alerts/AlertListPage'
import { AlertAllListPage } from '@/pages/alerts/AlertAllListPage'
import { AlertDetailPage } from '@/pages/alerts/AlertDetailPage'
import { AlertEditPage } from '@/pages/alerts/AlertEditPage'
import { AlertNewPage } from '@/pages/alerts/AlertNewPage'
import { AlertHistoryPage } from '@/pages/alerts/AlertHistoryPage'
import { AccountListPage } from '@/pages/accounts/AccountListPage'
import { AccountDetailPage } from '@/pages/accounts/AccountDetailPage'
import { AccountEditPage } from '@/pages/accounts/AccountEditPage'
import AccountNewPage from '@/pages/accounts/AccountNewPage'
import { UserListPage } from '@/pages/users/UserListPage'
import { UserNewPage } from '@/pages/users/UserNewPage'
import { UserDetailPage } from '@/pages/users/UserDetailPage'
import { UserEditPage } from '@/pages/users/UserEditPage'
import UserProfilePage from '@/pages/profile/UserProfilePage'
import SettingsPage from '@/pages/settings/SettingsPage'
import { ContactPage } from '@/pages/support/ContactPage'
import { ContactSentPage } from '@/pages/support/ContactSentPage'
import { DownloadCenterPage } from '@/pages/support/DownloadCenterPage'
import { ErrorPage } from '@/pages/errors/ErrorPage'
import { UnauthorizedPage } from '@/pages/errors/UnauthorizedPage'
import { DeviceTypeListPage } from '@/pages/admin/DeviceTypeListPage'
import { DeviceTypeEditPage } from '@/pages/admin/DeviceTypeEditPage'
import { SystemLogsPage } from '@/pages/admin/SystemLogsPage'
import { AdminNocDashboardPage } from '@/pages/admin/AdminNocDashboardPage'
import { GlobalSearchPage } from '@/pages/search/GlobalSearchPage'
import { MainLayout } from '@/components/layout/MainLayout'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/config/routes'
import { setQueryClient } from '@/services/api'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry auth errors — the interceptor already cleared the session
        if (error instanceof Error && 'response' in error) {
          const status = (error as any).response?.status
          if (status === 401 || status === 403) return false
        }
        return failureCount < 1
      },
      staleTime: 30000,
      gcTime: 5 * 60 * 1000, // Clean up unused query cache after 5 minutes
    }
  }
})

// Wire queryClient into API interceptor so 401s can clear the cache
setQueryClient(queryClient)

interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, _hasHydrated } = useAuthStore()

  // Wait for zustand to rehydrate from localStorage before making routing decisions
  if (!_hasHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  return <ErrorBoundary><MainLayout>{children}</MainLayout></ErrorBoundary>
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path={ROUTES.LOGIN} element={<LoginPage />} />
          <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/reset-password-invalid" element={<ResetPasswordInvalidPage />} />
          <Route path="/auth/reset-password-sent" element={<ResetPasswordSentPage />} />
          <Route path="/auth/invite-already-used" element={<InviteAlreadyUsedPage />} />
          <Route path="/auth/invite-invalid" element={<InviteInvalidPage />} />

          {/* Protected Routes */}
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/metrics"
            element={
              <ProtectedRoute>
                <MetricsDashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Device Routes */}
          <Route
            path={ROUTES.DEVICES}
            element={
              <ProtectedRoute>
                <DeviceListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DEVICE_NEW}
            element={
              <ProtectedRoute>
                <DeviceNewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DEVICE_CLAIM_CODES}
            element={
              <ProtectedRoute>
                <DeviceClaimCodesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/devices/:deviceId"
            element={
              <ProtectedRoute>
                <DeviceDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/devices/:deviceId/edit"
            element={
              <ProtectedRoute>
                <DeviceEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/devices/:deviceId/history"
            element={
              <ProtectedRoute>
                <DeviceHistoryPage />
              </ProtectedRoute>
            }
          />

          {/* Alert Routes */}
          <Route
            path={ROUTES.ALERTS}
            element={
              <ProtectedRoute>
                <AlertListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ALERT_NEW}
            element={
              <ProtectedRoute>
                <AlertNewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts/:alertId"
            element={
              <ProtectedRoute>
                <AlertDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts/:alertId/edit"
            element={
              <ProtectedRoute>
                <AlertEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts/history"
            element={
              <ProtectedRoute>
                <AlertHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts/all"
            element={
              <ProtectedRoute>
                <AlertAllListPage />
              </ProtectedRoute>
            }
          />

          {/* Account Routes */}
          <Route
            path={ROUTES.ACCOUNTS}
            element={
              <ProtectedRoute>
                <AccountListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ACCOUNT_NEW}
            element={
              <ProtectedRoute>
                <AccountNewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/:accountId"
            element={
              <ProtectedRoute>
                <AccountDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/:accountId/edit"
            element={
              <ProtectedRoute>
                <AccountEditPage />
              </ProtectedRoute>
            }
          />

          {/* User Routes */}
          <Route
            path={ROUTES.USERS}
            element={
              <ProtectedRoute>
                <UserListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.USER_PROFILE}
            element={
              <ProtectedRoute>
                <UserProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.USER_NEW}
            element={
              <ProtectedRoute>
                <UserNewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:userId"
            element={
              <ProtectedRoute>
                <UserDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:userId/edit"
            element={
              <ProtectedRoute>
                <UserEditPage />
              </ProtectedRoute>
            }
          />

          {/* Settings Route */}
          <Route
            path={ROUTES.SETTINGS}
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/device-types"
            element={
              <ProtectedRoute>
                <DeviceTypeListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/device-types/:deviceTypeId/edit"
            element={
              <ProtectedRoute>
                <DeviceTypeEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/device-types/:deviceTypeId"
            element={
              <ProtectedRoute>
                <DeviceTypeEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/logs"
            element={
              <ProtectedRoute>
                <SystemLogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN_NOC}
            element={
              <ProtectedRoute>
                <AdminNocDashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Search Routes */}
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <GlobalSearchPage />
              </ProtectedRoute>
            }
          />

          {/* Support Routes */}
          <Route
            path="/contact"
            element={
              <ProtectedRoute>
                <ContactPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contact/sent"
            element={
              <ProtectedRoute>
                <ContactSentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/downloads"
            element={
              <ProtectedRoute>
                <DownloadCenterPage />
              </ProtectedRoute>
            }
          />

          {/* Error Routes */}
          <Route path="/error" element={<ErrorPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
